'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useBehaviorSubjectUpdater } from '@ps/functions/hooks/useBehaviorSubject';
import { makeDebug } from '@ps/functions/utils/debug';
import { type Draft, produce } from 'immer';
import { BehaviorSubject, Subject, debounceTime, skip } from 'rxjs';

import { isChatDebugEnabled } from '../../functions/debug';
import { privateMessageEventDataToChatMessage } from '../../functions/privateMessageEventDataToChatMessage';
import { useChatContext } from '../../hooks/useChatContext';
import { useChatGatewayEvent } from '../../hooks/useChatGateway';
import { useChatMessageAPI } from '../../hooks/useChatMessageAPI';
import {
  useChatStorage,
  useGlobalChatContext,
} from '../../hooks/useGlobalChat';
import { usePendingMessages } from '../../hooks/usePendingMessages';
import {
  type ChatMessage,
  ChatMessageType,
  type ClientChatMessage,
  MsgSendStatus,
} from '../../types/ChatMessage';
import { ChatSessionType } from '../../types/ChatSessionType';
import { SocketEvent } from '../../types/SocketEvent';
import {
  ChatMessageContext,
  type ChatMessageContextValue,
  type ChatMessagesStatus,
} from './context';

const kCachedMessagesEnabled = true;
const kLimit = 100;

const debug = makeDebug(
  'chat:message-context',
  isChatDebugEnabled('messageContext'),
);

/**
 * Drives the per-room messages list:
 *
 *  1. Hydrate from local cache, then fetch fresh from the API.
 *  2. Fold incoming `private.message` gateway events into the list
 *     (replace if same `clientId`, else insert by time).
 *  3. Fold chat events from the global `events$` subject — `sending`
 *     messages appear instantly; failures get flagged; expired or
 *     pending-removed messages get patched out.
 *  4. Debounced persist to `ChatStorageManager` so a re-enter doesn't
 *     flicker.
 */
export function ChatMessageProvider({ children }: { children?: ReactNode }) {
  const storage = useChatStorage();
  const { events$ } = useGlobalChatContext();
  const { sessionType, roomId, currentUserId, targetUserId } = useChatContext();
  const messageAPI = useChatMessageAPI();
  const pendingMessages = usePendingMessages(roomId);

  const [messages$] = useState(() => new BehaviorSubject<ChatMessage[]>([]));
  const [newMsgReceived$] = useState(() => new Subject<ChatMessage>());
  const [status$] = useState(
    () =>
      new BehaviorSubject<ChatMessagesStatus>({
        isLoading: false,
        error: undefined,
        hasMore: false,
      }),
  );
  const updateStatus = useBehaviorSubjectUpdater(status$);

  // Read the LATEST pending messages without making them a reactive dependency
  // of the fetch. Sending a message adds then removes a pending entry, so if the
  // load callbacks depended on `pendingMessages` the whole list would re-fetch
  // (isLoading → messages cleared) the moment a send completed. Ongoing pending
  // changes are already folded in reactively by the `events$` subscription
  // below; the splice here only SEEDS in-flight messages into a fresh load.
  const pendingMessagesRef = useRef(pendingMessages);
  useEffect(() => {
    pendingMessagesRef.current = pendingMessages;
  }, [pendingMessages]);

  // Splice pending (in-flight) messages into a freshly-loaded list
  // unless they're already present by clientId.
  const withPendingMessagesApplied = useCallback(
    (loaded: ChatMessage[]): ChatMessage[] => {
      if (sessionType !== ChatSessionType.Private) return loaded;
      const seen = new Set(loaded.map(x => x.clientId));
      const merged = loaded.slice();
      for (const pending of pendingMessagesRef.current) {
        if (seen.has(pending.clientId)) continue;
        const pos = getMessageInsertionPos(merged, pending);
        merged.splice(pos, 0, pending);
      }
      return merged;
    },
    [sessionType],
  );

  const loadMessages = useCallback(async () => {
    debug('loadMessages start, room=%s', roomId);
    const cached = kCachedMessagesEnabled
      ? await storage.getMessages(roomId)
      : [];
    messages$.next(withPendingMessagesApplied(cached.slice(0, kLimit)));

    updateStatus(x => ({ ...x, isLoading: true, error: undefined }));
    try {
      const fresh = await messageAPI.fetchMessages(sessionType, {
        limit: kLimit,
      });
      messages$.next(withPendingMessagesApplied(fresh));
      updateStatus(x => ({
        ...x,
        isLoading: false,
        hasMore: fresh.length === kLimit,
      }));
    } catch (error) {
      console.error('error fetch fresh messages:', error);
      updateStatus(x => ({ ...x, isLoading: false, error }));
    }
  }, [
    messageAPI,
    messages$,
    roomId,
    sessionType,
    storage,
    updateStatus,
    withPendingMessagesApplied,
  ]);

  useEffect(() => {
    void loadMessages();
  }, [loadMessages]);

  // Debounced persist + final save on unmount.
  useEffect(() => {
    const sub = messages$
      .pipe(skip(1), debounceTime(3000))
      .subscribe(messages => {
        void storage.saveMessages(roomId, messages);
      });
    return () => {
      sub.unsubscribe();
      void storage.saveMessages(roomId, messages$.getValue());
    };
  }, [messages$, roomId, storage]);

  const loadMoreMessages = useCallback(async () => {
    const loaded = messages$.getValue();
    if (loaded.length === 0 || !targetUserId) return;
    const lastMessageId = loaded[loaded.length - 1].messageId;
    updateStatus(x => ({ ...x, isLoadingMore: true, error: undefined }));
    try {
      const more = await messageAPI.fetchMessages(sessionType, {
        lastMessageId:
          typeof lastMessageId === 'number' ? lastMessageId : undefined,
        limit: kLimit,
      });
      messages$.next(withPendingMessagesApplied([...loaded, ...more]));
      updateStatus(x => ({
        ...x,
        isLoadingMore: false,
        hasMore: more.length === kLimit,
      }));
    } catch (error) {
      console.error('error fetch more messages:', error);
      updateStatus(x => ({ ...x, isLoadingMore: false, error }));
    }
  }, [
    messageAPI,
    messages$,
    sessionType,
    targetUserId,
    updateStatus,
    withPendingMessagesApplied,
  ]);

  useChatGatewayEvent(
    SocketEvent.NewMsg,
    raw => {
      const message = privateMessageEventDataToChatMessage(raw);
      if (
        !shouldAcceptMessage(sessionType, message, currentUserId, targetUserId)
      ) {
        return;
      }
      const messages = messages$.getValue();
      const updated = produce(messages, draft => {
        const index = messages.findIndex(x => x.clientId === message.clientId);
        if (index >= 0) {
          draft[index] = message;
        } else {
          const pos = getMessageInsertionPos(messages, message);
          draft.splice(pos, 0, message);
        }
      });
      if (messages !== updated) {
        messages$.next(updated);
      }
      newMsgReceived$.next(message);
    },
    'chat-message-context',
  );

  // Fold global chat-events (sending/failed/expired/pending-removed)
  // into the per-room list.
  useEffect(() => {
    const sub = events$.subscribe(event => {
      const messages = messages$.getValue();
      const updated = produce(messages, draft => {
        switch (event.type) {
          case 'sending-message':
            if (
              !shouldAcceptMessage(
                sessionType,
                event.message,
                currentUserId,
                targetUserId,
              )
            )
              return;
            upsertMessage(draft, event.message);
            break;
          case 'send-message-failed': {
            if (
              !shouldAcceptMessage(
                sessionType,
                event.message,
                currentUserId,
                targetUserId,
              )
            )
              return;
            const lastSendErrorMsg =
              typeof event.error === 'string'
                ? event.error
                : ((event.error as Error)?.message ?? 'Unknown error');
            upsertMessage(draft, {
              ...event.message,
              sendStatus: MsgSendStatus.fail,
              lastSendErrorMsg,
            } as ClientChatMessage);
            break;
          }
          case 'messages-expired': {
            const map = new Map(event.messages.map(x => [x.clientId, x]));
            draft.forEach((m, i) => {
              const expired = map.get(m.clientId);
              if (expired) draft[i] = expired;
            });
            break;
          }
          case 'pending-message-removed': {
            if (
              !shouldAcceptMessage(
                sessionType,
                event.message,
                currentUserId,
                targetUserId,
              )
            )
              return;
            const index = draft.findIndex(
              x => x.clientId === event.message.clientId,
            );
            if (index >= 0) draft.splice(index, 1);
            break;
          }
        }
      });
      if (messages !== updated) {
        messages$.next(updated);
      }
    });
    return () => sub.unsubscribe();
  }, [currentUserId, events$, messages$, sessionType, targetUserId]);

  const value = useMemo<ChatMessageContextValue>(
    () => ({ messages$, status$, newMsgReceived$, loadMoreMessages }),
    [messages$, status$, newMsgReceived$, loadMoreMessages],
  );

  return (
    <ChatMessageContext.Provider value={value}>
      {children}
    </ChatMessageContext.Provider>
  );
}

function upsertMessage(draft: Draft<ChatMessage[]>, message: ChatMessage) {
  const index = draft.findIndex(x => x.clientId === message.clientId);
  if (index >= 0) {
    const existing = draft[index] as ClientChatMessage;
    const incoming = message as ClientChatMessage;
    if (
      existing.messageId !== incoming.messageId ||
      existing.sendStatus !== incoming.sendStatus ||
      existing.lastSendErrorMsg !== incoming.lastSendErrorMsg
    ) {
      draft[index] = message;
    }
  } else {
    const pos = getMessageInsertionPos(draft, message);
    draft.splice(pos, 0, message);
  }
}

/**
 * Index where `msg` should be inserted into a time-descending list
 * (newest first) such that ordering by `time` is preserved. Matches
 * legacy semantics; the existing entry with the same `clientId`
 * collapses onto itself.
 */
function getMessageInsertionPos(
  messages: ChatMessage[] | Draft<ChatMessage[]>,
  msg: ChatMessage,
): number {
  for (let i = 0; i < messages.length; i++) {
    if (msg.clientId === messages[i].clientId || msg.time > messages[i].time) {
      return i;
    }
  }
  return 0;
}

/**
 * True when the message belongs to *this* room — same session type,
 * and the from/to pair matches the currentUser ↔ targetUser pair.
 * Mirrors the legacy filter.
 */
function shouldAcceptMessage(
  sessionType: ChatSessionType,
  msg: ChatMessage,
  currentUserId: string,
  targetUserId: string,
): boolean {
  if (msg.sessionType !== sessionType) return false;
  if (
    msg.type === ChatMessageType.SystemMessage &&
    msg.to?.userId === currentUserId
  ) {
    return true;
  }
  const fromMe = msg.from.userId === currentUserId;
  const toMe = msg.to?.userId === currentUserId;
  if (!fromMe && !toMe) return false;
  return (
    (toMe && msg.from.userId === targetUserId) ||
    (fromMe && msg.to?.userId === targetUserId)
  );
}
